import React, { useEffect, useRef, useState } from "react";
import { IconContext } from "react-icons";
import { AiFillPauseCircle, AiFillPlayCircle } from "react-icons/ai";
import { musicDB } from "../../resources/musicData";
import "./musicPlayer.css";
import "./progressBar.css";

const MusicPlayer = (props) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);
  const [currentSongIndex] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentCoverIndex, setCurrentCoverIndex] = useState(0);
  const [autoCarouselCount, setAutoCarouselCount] = useState(0);

  // Bonus is "unlocked" once - stays unlocked until page refresh
  const [bonusUnlocked, setBonusUnlocked] = useState(false);
  const { hasReachedLastLyric = false } = props;

  // One-way latch: once last lyric is reached, bonus stays unlocked
  useEffect(() => {
    if (hasReachedLastLyric && !bonusUnlocked) {
      setBonusUnlocked(true);
      // Auto-switch to bonus image when it unlocks
      setCurrentCoverIndex(2);
      // Stop auto-carousel
      setAutoCarouselCount(Infinity);
    }
  }, [hasReachedLastLyric, bonusUnlocked]);

  // Cover images - bonus only included when unlocked
  const baseCoverImages = ["/AlbumCover.jpeg", "/BackCover.png"];
  const baseCoverLabels = ["Front", "Back"];
  const coverImages = bonusUnlocked
    ? [...baseCoverImages, "/tj-and-maryann.jpeg"]
    : baseCoverImages;
  const coverLabels = bonusUnlocked
    ? [...baseCoverLabels, "Bonus"]
    : baseCoverLabels;
  const maxAutoCarouselCycles = 2; // Show back once, return to front, then stop

  // Auto-carousel: cycle through album art every 5 seconds, limited cycles
  useEffect(() => {
    if (autoCarouselCount >= maxAutoCarouselCycles) return;

    const interval = setInterval(() => {
      setCurrentCoverIndex((prev) => (prev + 1) % coverImages.length);
      setAutoCarouselCount((count) => count + 1);
    }, 5000);

    return () => clearInterval(interval);
  }, [autoCarouselCount, coverImages.length]);

  const togglePlay = () => {
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  useEffect(() => {
    // Reset album art to the first image when the song changes
    setCurrentCoverIndex(0);
  }, [currentSongIndex]);

  const handleTimeUpdate = (event) => {
    const newTime = event.target.currentTime;
    setCurrentTime(newTime);

    // Send time updates to parent for lyric syncing
    props.getDataForLyrics({
      trackId: musicDB[currentSongIndex].id,
      currentTime: newTime,
    });
  };

  const handleLoadedMetadata = (event) => {
    setDuration(event.target.duration);
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
    // Don't reset cover - keep showing bonus if unlocked
  };

  const formatTime = (time) => {
    if (!Number.isFinite(time) || time < 0) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  const handleSeek = (newTime) => {
    if (!Number.isFinite(newTime)) return;

    const clampedTime = Math.max(0, Math.min(newTime, duration || 0));
    audioRef.current.currentTime = clampedTime;
    setCurrentTime(clampedTime);

    // Keep lyrics in sync immediately when seeking.
    props.getDataForLyrics({
      trackId: musicDB[currentSongIndex].id,
      currentTime: clampedTime,
    });
  };
  const progress = duration > 0 ? Math.min((currentTime / duration) * 100, 100) : 0;

  const currentCover = coverImages[currentCoverIndex];

  const handleSelectCover = (index) => {
    setCurrentCoverIndex(index);
    setAutoCarouselCount(maxAutoCarouselCycles); // Stop auto-carousel when user interacts
  };

  const handlePrevCover = () => {
    setCurrentCoverIndex((prev) => (prev - 1 + coverImages.length) % coverImages.length);
    setAutoCarouselCount(maxAutoCarouselCycles);
  };

  const handleNextCover = () => {
    setCurrentCoverIndex((prev) => (prev + 1) % coverImages.length);
    setAutoCarouselCount(maxAutoCarouselCycles);
  };

  return (
    <div className="music-player">
      <audio
        ref={audioRef}
        src={musicDB[currentSongIndex].src}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
      ></audio>
      <div className="component">
        <div className="musicCover">
          <img className="albumArtImage" src={currentCover} alt="Album Cover" />
          <div className="carousel-controls">
            <button
              className="carousel-arrow"
              onClick={handlePrevCover}
              aria-label="Previous image"
            >
              ◀
            </button>
            <div className="carousel-tabs">
              {coverImages.map((_, index) => (
                <button
                  key={index}
                  className={`carousel-tab ${
                    index === currentCoverIndex ? "active" : ""
                  }`}
                  onClick={() => handleSelectCover(index)}
                >
                  {coverLabels[index]}
                </button>
              ))}
            </div>
            <button
              className="carousel-arrow"
              onClick={handleNextCover}
              aria-label="Next image"
            >
              ▶
            </button>
          </div>
          <p className="carousel-hint">Click tabs or arrows to flip album</p>
        </div>
        <div className="progress-container">
          <div
            className="progress"
            onClick={(e) => {
              if (!Number.isFinite(duration) || duration <= 0) return;

              const rect = e.currentTarget.getBoundingClientRect();
              const clickX = e.clientX - rect.left;
              const ratio = rect.width > 0 ? clickX / rect.width : 0;

              handleSeek(ratio * duration);
            }}
          >
            <div
              className="progress-filled"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>
        <div className="track-info">
          <div className="current-time">{formatTime(currentTime)}</div>
          <div className="duration">{formatTime(duration)}</div>
        </div>
        <div className="musicDetails">
          <h3 className="title">{musicDB[currentSongIndex].title}</h3>
          <p className="subTitle">{musicDB[currentSongIndex].artist}</p>
        </div>
        <div className="musicControls">
          {!isPlaying ? (
            <button className="playButton clickable" onClick={togglePlay}>
              <IconContext.Provider value={{ size: "3em", color: "#000000" }}>
                <AiFillPlayCircle />
              </IconContext.Provider>
            </button>
          ) : (
            <button className="playButton clickable" onClick={togglePlay}>
              <IconContext.Provider value={{ size: "3em", color: "#000000" }}>
                <AiFillPauseCircle />
              </IconContext.Provider>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MusicPlayer;
